import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import SuperadminLayout from './SuperadminLayout';
import { ordenarListaAlfabetica } from './superadminSortUtils';
import './SuperadminStyles.css';

const ParametrosFev = () => {
    const history = useHistory();
    const [resoluciones, setResoluciones] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    
    // Filtros
    const [filtros, setFiltros] = useState({
        client_id: '',
        users_id: '',
        estado: '',
        tipo: '',
        busqueda: ''
    });

    // Modal de edición
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingResolucion, setEditingResolucion] = useState(null);
    const [editForm, setEditForm] = useState({
        habilitado: 0,
        consumo: 0,
        estado: 1
    });

    // Modal de recarga
    const [showRecargarModal, setShowRecargarModal] = useState(false);
    const [recargandoId, setRecargandoId] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchData();
    }, [history]);

    const fetchData = async () => {
        try {
            const [resolucionesRes, clientesRes] = await Promise.all([
                axios.get('/superadmin/parametros-fev'),
                axios.get('/superadmin/clientes')
            ]);

            setResoluciones(ordenarListaAlfabetica(resolucionesRes.data, 'cliente_nombre'));
            setClientes(ordenarListaAlfabetica(Array.isArray(clientesRes.data) ? clientesRes.data : [], 'nombre'));
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            } else {
                setAlert({
                    show: true,
                    type: 'danger',
                    message: 'Error al cargar los datos'
                });
                setLoading(false);
            }
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post('/superadmin/logout');
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            localStorage.removeItem('superadmin_token');
            localStorage.removeItem('superadmin_user');
            delete axios.defaults.headers.common['Authorization'];
            history.push('/superadmin/login');
        }
    };

    const handleFiltroChange = async (campo, valor) => {
        const nuevosFiltros = { ...filtros, [campo]: valor };
        setFiltros(nuevosFiltros);

        // Si cambia el cliente, cargar usuarios de ese cliente
        if (campo === 'client_id' && valor) {
            try {
                const response = await axios.get(`/superadmin/clientes/${valor}/usuarios`);
                setUsuarios(Array.isArray(response.data) ? response.data : []);
                nuevosFiltros.users_id = ''; // Reset usuario filter
            } catch (error) {
                console.error('Error loading usuarios:', error);
                setUsuarios([]);
            }
        }

        if (campo === 'client_id' && !valor) {
            setUsuarios([]);
            nuevosFiltros.users_id = '';
        }

        // Aplicar filtros
        aplicarFiltros(nuevosFiltros);
    };

    const aplicarFiltros = async (filtrosActuales = filtros) => {
        try {
            const params = {};
            if (filtrosActuales.client_id) params.client_id = filtrosActuales.client_id;
            if (filtrosActuales.users_id) params.users_id = filtrosActuales.users_id;
            if (filtrosActuales.estado !== '') params.estado = filtrosActuales.estado;
            if (filtrosActuales.tipo) params.tipo = filtrosActuales.tipo;
            if (filtrosActuales.busqueda) params.busqueda = filtrosActuales.busqueda;

            const response = await axios.get('/superadmin/parametros-fev', { params });
            setResoluciones(response.data);
        } catch (error) {
            console.error('Error applying filters:', error);
        }
    };

    const handleEditClick = (resolucion) => {
        setEditingResolucion(resolucion);
        setEditForm({
            habilitado: resolucion.habilitado || 0,
            consumo: resolucion.consumo || 0,
            estado: resolucion.estado || 1
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        try {
            await axios.put(`/superadmin/parametros-fev/${editingResolucion.id}`, editForm);
            
            setAlert({
                show: true,
                type: 'success',
                message: 'Resolución actualizada exitosamente'
            });
            
            setShowEditModal(false);
            fetchData();
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
        } catch (error) {
            console.error('Error updating resolucion:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al actualizar resolución'
            });
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const handleRecargarClick = (resolucion) => {
        setRecargandoId(resolucion.id);
        setEditingResolucion(resolucion);
        setShowRecargarModal(true);
    };

    const handleRecargarConfirm = async () => {
        try {
            await axios.post(`/superadmin/parametros-fev/${recargandoId}/recargar`);
            
            setAlert({
                show: true,
                type: 'success',
                message: 'Consumo reseteado exitosamente'
            });
            
            setShowRecargarModal(false);
            fetchData();
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
        } catch (error) {
            console.error('Error recargando:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al recargar consumo'
            });
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const getEstadoBadge = (resolucion) => {
        if (!resolucion.estado) {
            return <span className="badge badge-secondary">Inactiva</span>;
        }
        
        const disponibles = (resolucion.habilitado || 0) - (resolucion.consumo || 0);
        
        if (disponibles <= 0) {
            return <span className="badge badge-danger">Agotada</span>;
        } else if (disponibles <= 10) {
            return <span className="badge badge-warning">Por Agotarse</span>;
        } else {
            return <span className="badge badge-success">Activa</span>;
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-file-invoice"></i> Parámetros FEV - Facturación Electrónica
                </h4>
            </div>

            {/* Alertas */}
            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                                    {alert.message}
                                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                            )}

            {/* Filtros */}
            <div className="card mb-4">
                <div className="card-header">
                    <h5 className="mb-0"><i className="fas fa-filter"></i> Filtros</h5>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label>Cliente</label>
                            <select
                                className="form-control"
                                value={filtros.client_id}
                                onChange={(e) => handleFiltroChange('client_id', e.target.value)}
                            >
                                <option value="">Todos los clientes</option>
                                {clientes.map(c => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label>Usuario</label>
                            <select
                                className="form-control"
                                value={filtros.users_id}
                                onChange={(e) => handleFiltroChange('users_id', e.target.value)}
                                disabled={!filtros.client_id}
                            >
                                <option value="">Todos los usuarios</option>
                                {usuarios.map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label>Estado</label>
                            <select
                                className="form-control"
                                value={filtros.estado}
                                onChange={(e) => handleFiltroChange('estado', e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="1">Activas</option>
                                <option value="0">Inactivas</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label>Tipo</label>
                            <select
                                className="form-control"
                                value={filtros.tipo}
                                onChange={(e) => handleFiltroChange('tipo', e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="FE">FE</option>
                                <option value="NC">NC</option>
                                <option value="DS">DS</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label>Buscar</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Resolución/Prefijo"
                                value={filtros.busqueda}
                                onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabla de Resoluciones */}
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Usuario</th>
                                    <th>Resolución</th>
                                    <th>Prefijo</th>
                                    <th>Tipo</th>
                                    <th>Habilitado</th>
                                    <th>Consumo</th>
                                    <th>Disponibles</th>
                                    <th>Total Histórico</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resoluciones.length > 0 ? (
                                    resoluciones.map(res => {
                                        const disponibles = (res.habilitado || 0) - (res.consumo || 0);
                                        const porcentaje = res.habilitado > 0 
                                            ? ((res.consumo || 0) / res.habilitado * 100).toFixed(1)
                                            : 0;
                                        
                                        return (
                                            <tr key={res.id}>
                                                <td>
                                                    <small className="text-muted">{res.cliente_nombre}</small>
                                                </td>
                                                <td>
                                                    <small className="text-muted">{res.usuario_nombre}</small>
                                                </td>
                                                <td>{res.resolucion}</td>
                                                <td><span className="badge badge-info">{res.prefijo}</span></td>
                                                <td>{res.tipo}</td>
                                                <td className="text-center">{res.habilitado || 0}</td>
                                                <td className="text-center">
                                                    {res.consumo || 0}
                                                    <br />
                                                    <small className="text-muted">({porcentaje}%)</small>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge badge-${disponibles <= 0 ? 'danger' : disponibles <= 10 ? 'warning' : 'success'}`}>
                                                        {disponibles}
                                                    </span>
                                                </td>
                                                <td className="text-center">{res.contador || 0}</td>
                                                <td>{getEstadoBadge(res)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-dark mr-1"
                                                        onClick={() => handleEditClick(res)}
                                                        title="Editar"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => handleRecargarClick(res)}
                                                        title="Recargar consumo"
                                                        disabled={!res.estado}
                                                    >
                                                        <i className="fas fa-sync-alt"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="text-center text-muted">
                                            No se encontraron resoluciones
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal de Edición */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Editar Resolución FEV</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingResolucion && (
                        <>
                            <div className="mb-3">
                                <p><strong>Cliente:</strong> {editingResolucion.cliente_nombre}</p>
                                <p><strong>Usuario:</strong> {editingResolucion.usuario_nombre}</p>
                                <p><strong>Resolución:</strong> {editingResolucion.resolucion}</p>
                                <p><strong>Prefijo:</strong> {editingResolucion.prefijo}</p>
                            </div>
                            <hr />
                            <div className="form-group">
                                <label>Documentos Habilitados (Plan)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editForm.habilitado}
                                    onChange={(e) => setEditForm({ ...editForm, habilitado: parseInt(e.target.value) || 0 })}
                                    min="0"
                                />
                                <small className="form-text text-muted">
                                    Cantidad de documentos permitidos en el periodo actual
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Consumo Actual</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={editForm.consumo}
                                    onChange={(e) => setEditForm({ ...editForm, consumo: parseInt(e.target.value) || 0 })}
                                    min="0"
                                />
                                <small className="form-text text-muted">
                                    Documentos ya consumidos (normalmente se resetea con el botón Recargar)
                                </small>
                            </div>
                            <div className="form-group">
                                <label>Estado</label>
                                <select
                                    className="form-control"
                                    value={editForm.estado}
                                    onChange={(e) => setEditForm({ ...editForm, estado: parseInt(e.target.value) })}
                                >
                                    <option value="1">Activa</option>
                                    <option value="0">Inactiva</option>
                                </select>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="dark" onClick={handleEditSubmit}>
                        <i className="fas fa-save"></i> Guardar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Recarga */}
            <Modal show={showRecargarModal} onHide={() => setShowRecargarModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Recargar Consumo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingResolucion && (
                        <>
                            <div className="alert alert-warning">
                                <i className="fas fa-exclamation-triangle"></i> Esta acción reseteará el consumo a 0, permitiendo al cliente emitir documentos nuevamente.
                            </div>
                            <p><strong>Cliente:</strong> {editingResolucion.cliente_nombre}</p>
                            <p><strong>Usuario:</strong> {editingResolucion.usuario_nombre}</p>
                            <p><strong>Resolución:</strong> {editingResolucion.resolucion}</p>
                            <p><strong>Prefijo:</strong> {editingResolucion.prefijo}</p>
                            <p><strong>Consumo actual:</strong> {editingResolucion.consumo || 0} / {editingResolucion.habilitado || 0}</p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRecargarModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="success" onClick={handleRecargarConfirm}>
                        <i className="fas fa-sync-alt"></i> Confirmar Recarga
                    </Button>
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default ParametrosFev;
